import { useState, useEffect, useCallback } from 'react'
import {
  MdArrowBack,
  MdFlight,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdCheckCircle,
  MdPauseCircle,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, InfoCard, Badge } from '../../components/ui'
import { useAirport } from '../../hooks/useAirport'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function AirportDetailPage() {
  const dispatch = useDispatch()
  const { airportId } = useParams()
  const navigate = useNavigate()
  const [airport, setAirport] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const { getAirportByIdAsync, isGetAirportByIdLoading } = useAirport()

  const loadAirport = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const airportData = await getAirportByIdAsync(airportId)
      setAirport(airportData)
    } catch (error) {
      console.error('Error loading airport:', error)
      setError('Failed to load airport details')
    } finally {
      setIsLoading(false)
    }
  }, [airportId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (airportId) {
      loadAirport()
    }
  }, [airportId, loadAirport])

  useEffect(() => {
    const channelName = getWebsocketChannel(airportId).airportDetails

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    navigate(-1)
  }

  const handleEdit = () => {
    navigate(`/airports/${airportId}/edit`)
  }

  const handleDelete = () => {
    // TODO: Implement delete confirmation modal
    console.log('Delete airport:', airportId)
  }

  const handleRefresh = () => {
    loadAirport()
  }

  const getStatusIcon = (isActive) => {
    return isActive ? (
      <MdCheckCircle className="text-success-600 dark:text-success-400" />
    ) : (
      <MdPauseCircle className="text-neutral-600 dark:text-neutral-400" />
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !airport) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <MdFlight className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-white">
            {error || 'Airport not found'}
          </h3>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            The airport you're looking for doesn't exist or has been removed.
          </p>
          <div className="mt-6">
            <Button variant="primary" onClick={handleBack}>
              Back to Airports
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-start mb-4">
          <Button variant="ghost" onClick={handleBack} className="flex items-center space-x-2">
            <MdArrowBack />
            <span>Back</span>
          </Button>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 min-w-0 text-left">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                {airport.name}
              </h1>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(airport.isActive)}
                  <Badge variant={airport.isActive ? 'success' : 'default'}>
                    {airport.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <span className="text-neutral-600 dark:text-neutral-400">{airport.code}</span>
                <span className="text-neutral-600 dark:text-neutral-400">
                  {airport.city}, {airport.stateCode || airport.state}
                </span>
                <span className="text-neutral-600 dark:text-neutral-400">{airport.country}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetAirportByIdLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetAirportByIdLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="secondary"
              onClick={handleEdit}
              className="flex items-center space-x-2"
            >
              <MdEdit className="text-lg" />
              <span>Edit</span>
            </Button>
            <Button variant="danger" onClick={handleDelete} className="flex items-center space-x-2">
              <MdDelete className="text-lg" />
              <span>Delete</span>
            </Button>
          </div>
        </div>
      </div>
      {/* Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <InfoCard title="General Info">
          <div className="space-y-2 text-neutral-900 dark:text-white">
            <div>
              <strong className="text-neutral-900 dark:text-white">Code:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">{airport.code}</span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Type:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">{airport.type}</span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Timezone:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">{airport.timezone}</span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Elevation:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">{airport.elevation} ft</span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Coordinates:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">
                {airport.latitude}, {airport.longitude}
              </span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Created At:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">
                {airport.createdAt && new Date(airport.createdAt).toLocaleString()}
              </span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Last Updated:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">
                {airport.updatedAt && new Date(airport.updatedAt).toLocaleString()}
              </span>
            </div>
          </div>
        </InfoCard>
        <InfoCard title="Location">
          <div className="space-y-2 text-neutral-900 dark:text-white">
            <div>
              <strong className="text-neutral-900 dark:text-white">City:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">{airport.city}</span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">State:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">
                {airport.state} ({airport.stateCode})
              </span>
            </div>
            <div>
              <strong className="text-neutral-900 dark:text-white">Country:</strong>{' '}
              <span className="text-neutral-700 dark:text-neutral-300">{airport.country}</span>
            </div>
          </div>
        </InfoCard>
      </div>
      {/* Runways */}
      <div className="mb-8">
        <InfoCard title="Runways">
          {airport.runways && airport.runways.length > 0 ? (
            <div className="space-y-2 text-neutral-900 dark:text-white">
              {airport.runways.map((runway, idx) => (
                <div
                  key={runway._id || idx}
                  className="border-b border-neutral-200 dark:border-neutral-700 py-2"
                >
                  <div>
                    <strong className="text-neutral-900 dark:text-white">Number:</strong>{' '}
                    <span className="text-neutral-700 dark:text-neutral-300">{runway.number}</span>
                  </div>
                  <div>
                    <strong className="text-neutral-900 dark:text-white">Length:</strong>{' '}
                    <span className="text-neutral-700 dark:text-neutral-300">
                      {runway.length} m
                    </span>
                  </div>
                  <div>
                    <strong className="text-neutral-900 dark:text-white">Width:</strong>{' '}
                    <span className="text-neutral-700 dark:text-neutral-300">{runway.width} m</span>
                  </div>
                  <div>
                    <strong className="text-neutral-900 dark:text-white">Surface:</strong>{' '}
                    <span className="text-neutral-700 dark:text-neutral-300">{runway.surface}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-neutral-700 dark:text-neutral-300">No runways listed.</div>
          )}
        </InfoCard>
      </div>
    </div>
  )
}

export default AirportDetailPage
